import type { Notification } from '../types/notification.js'

/**
 * Ensures a notification has properly initialized properties
 * Prevents errors when working with notifications that might not have all properties
 *
 * @param notification The notification to normalize
 * @returns The normalized notification
 */
export function normalizeNotification(notification: Notification): Notification {
  if (!notification) return notification

  // Ensure metadata is an object
  if (!notification.metadata || typeof notification.metadata !== 'object') {
    notification.metadata = {}
  }

  // Set default values for optional fields if missing
  if (notification.linkType === undefined) {
    notification.linkType = 'none'
  }

  if (notification.linkTarget === undefined) {
    notification.linkTarget = ''
  }

  if (notification.priority === undefined) {
    notification.priority = 'normal'
  }

  if (notification.category === undefined) {
    notification.category = 'system'
  }

  if (notification.emailSent === undefined) {
    notification.emailSent = false
  }

  if (notification.emailScheduled === undefined) {
    notification.emailScheduled = false
  }

  if (notification.readAt === undefined) {
    notification.readAt = null
  }

  if (notification.emailSentAt === undefined) {
    notification.emailSentAt = null
  }
  return notification
}

/**
 * Formats a notification body for display, handling:
 * - Simple HTML (b, i, a tags)
 * - Newlines to <br> tags
 * - URL auto-linking
 *
 * @param body The notification body text
 * @returns Safely formatted HTML
 */
export function formatNotificationBody(body: string): string {
  if (!body) return ''

  // Convert URLs to links (basic implementation - could be enhanced)
  const linkedText = body.replace(
    /(https?:\/\/[^\s]+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>',
  )

  // Convert newlines to <br> tags
  return linkedText.replace(/\n/g, '<br>')
}

/**
 * Get appropriate icon for a notification category
 *
 * @param category Notification category
 * @returns PrimeVue icon class
 */
export function getNotificationCategoryIcon(category: string): string {
  switch (category) {
    case 'task':
      return 'pi-check-square'
    case 'membership':
      return 'pi-users'
    case 'admin':
      return 'pi-shield'
    case 'system':
    default:
      return 'pi-bell'
  }
}

/**
 * Get appropriate severity for a notification priority
 *
 * @param priority Notification priority
 * @returns PrimeVue severity class
 */
export function getNotificationPrioritySeverity(priority: string): string {
  switch (priority) {
    case 'high':
      return 'danger'
    case 'normal':
      return 'info'
    case 'low':
    default:
      return 'secondary'
  }
}
